import { useRef, useState } from 'react'

/**
 * Lead placeholders the backend substitutes per lead: inside writing samples
 * before they reach the AI prompt, and inside template messages at send time.
 *
 * Must stay in sync with v3/workers/ai/src/prompts/placeholders.ts.
 */
export const LEAD_PLACEHOLDERS = [
  '{first_name}',
  '{full_name}',
  '{last_name}',
  '{company}',
  '{title}',
  '{industry}',
  '{location}',
] as const

/**
 * A textarea with variable insertion (owner request 2026-09-21): nobody
 * should have to type "{company}" by hand. Clicking a variable chip drops it
 * at the cursor, and typing "{" opens a filtered list that completes on
 * Enter, Tab or click. Used by the writing-sample editor and the template
 * editor (card S3).
 */
export function VariableTextarea({
  value,
  placeholder,
  maxLength,
  className,
  ariaLabel,
  senderSlots,
  onChange,
}: {
  readonly value: string
  readonly placeholder?: string
  readonly maxLength: number
  /** Size classes for the textarea (height, text size). */
  readonly className: string
  readonly ariaLabel?: string
  /**
   * The sender's own [slots] (template editor only). When given, the chips
   * show in two labelled rows, "Lead" and "You", so the lead's first name
   * and the sender's are never confused.
   */
  readonly senderSlots?: readonly string[]
  readonly onChange: (next: string) => void
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null)
  const [query, setQuery] = useState<string | null>(null)
  const [highlight, setHighlight] = useState(0)

  const matches = query === null
    ? []
    : LEAD_PLACEHOLDERS.filter((t) => t.slice(1).startsWith(query.toLowerCase()))

  /** Replace the partial "{que" before the caret (or insert at the caret). */
  function insertToken(token: string) {
    const el = ref.current
    let caret = el?.selectionStart ?? value.length
    // Caret inside a finished token: insert after it instead of splitting it.
    const rest = /^[a-z_]*\}/i.exec(value.slice(caret))
    const insideToken = rest !== null && /\{[a-z_]*$/i.test(value.slice(0, caret))
    if (insideToken) caret += rest[0].length
    const before = value.slice(0, caret)
    const partial = insideToken ? null : /\{[a-z_]*$/i.exec(before)
    const start = partial ? caret - partial[0].length : caret
    const next = `${value.slice(0, start)}${token}${value.slice(caret)}`
    onChange(next)
    setQuery(null)
    requestAnimationFrame(() => {
      const pos = start + token.length
      el?.focus()
      el?.setSelectionRange(pos, pos)
    })
  }

  function refreshQuery(el: HTMLTextAreaElement) {
    const caret = el.selectionStart ?? 0
    const before = el.value.slice(0, caret)
    const m = /\{([a-z_]*)$/i.exec(before)
    // A caret inside a finished token ("{comp|any}") is not a new one: no list,
    // or completing it would leave "{company}any}".
    const insideToken = m !== null && /^[a-z_]*\}/i.test(el.value.slice(caret))
    setQuery(m && !insideToken ? (m[1] ?? '') : null)
    setHighlight(0)
  }

  return (
    <div>
      <textarea
        ref={ref}
        className={`w-full rounded-md border border-input bg-background px-2 py-1.5 resize-y shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)] placeholder:text-subtle-foreground hover:border-[#7C8399] focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/40 ${className}`}
        placeholder={placeholder}
        value={value}
        maxLength={maxLength}
        aria-label={ariaLabel}
        onChange={(e) => {
          onChange(e.target.value)
          refreshQuery(e.target)
        }}
        onClick={(e) => refreshQuery(e.currentTarget)}
        onBlur={() => window.setTimeout(() => setQuery(null), 120)}
        onKeyDown={(e) => {
          if (matches.length === 0) return
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setHighlight((h) => (h + 1) % matches.length)
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setHighlight((h) => (h - 1 + matches.length) % matches.length)
          } else if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault()
            insertToken(matches[highlight] ?? (matches[0] as string))
          } else if (e.key === 'Escape') {
            setQuery(null)
          }
        }}
      />
      {matches.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1 rounded-md border border-[#3A3E4F] bg-popover p-1 shadow-sm">
          {matches.map((token, i) => (
            <button
              key={token}
              type="button"
              className={[
                'rounded px-1.5 py-0.5 text-[11px] font-mono',
                i === highlight ? 'bg-primary text-primary-foreground' : 'text-secondary-foreground hover:bg-muted',
              ].join(' ')}
              onMouseDown={(e) => {
                e.preventDefault()
                insertToken(token)
              }}
            >
              {token}
            </button>
          ))}
        </div>
      )}
      <ChipRow
        label={senderSlots ? 'Lead' : undefined}
        tokens={LEAD_PLACEHOLDERS}
        title={(token) => (senderSlots ? `Insert the lead's ${token} at the cursor` : `Insert ${token} at the cursor`)}
        onInsert={insertToken}
      />
      {senderSlots && senderSlots.length > 0 && (
        <ChipRow
          label="You"
          tokens={senderSlots.map((slot) => `[${slot}]`)}
          title={(token) => `Insert ${token} at the cursor. You fill it in once, above.`}
          onInsert={insertToken}
        />
      )}
    </div>
  )
}

function ChipRow({
  label,
  tokens,
  title,
  onInsert,
}: {
  readonly label?: string
  readonly tokens: readonly string[]
  readonly title: (token: string) => string
  readonly onInsert: (token: string) => void
}) {
  return (
    <div className="mt-1 flex items-start gap-1">
      {label && <span className="w-9 shrink-0 pt-0.5 text-[10px] font-medium text-muted-foreground">{label}</span>}
      {/* Chips in their own column, so a wrapped row stays indented past the label. */}
      <div className="flex min-w-0 flex-wrap gap-1">
        {tokens.map((token) => (
          <button
            key={token}
            type="button"
            title={title(token)}
            className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono text-secondary-foreground hover:bg-primary hover:text-primary-foreground"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onInsert(token)}
          >
            {token}
          </button>
        ))}
      </div>
    </div>
  )
}
